import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  input,
  Input,
  Output,
} from '@angular/core';
import { catchError, combineLatestWith, map, Observable, of, tap } from 'rxjs';
import { BookingService, CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ServiceManager } from '../../service-responsibility-dialog/service-responsibility-dialog.component';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { StaticRole, UserOrganizationStatus } from '@markant-platform/shared/user/model';
import { BookingState } from '@markant-platform/cartridge/model';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'markant-platform-management-tile-service-responsibility',
  templateUrl: './service-responsibility.component.html',
  styleUrls: ['./service-responsibility.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceResponsibilityTileComponent {
  @Input({ required: true }) serviceManagerId!: string;
  @Input({ required: true }) bookingId!: string;
  @Input({ required: true }) serviceManager!: string;
  @Input({ required: true }) organizationId!: string;

  bookingState = input<BookingState>(BookingState.ACCEPTED);
  usageRightSuspended = input<boolean>(false);
  showEditButton = computed(() => this.getEditButtonVisibility(this.bookingState(), this.usageRightSuspended()));

  @Output() serviceManagerUpdated = new EventEmitter<void>();

  showDialog = false;
  isLoading = false;
  isAuthorized = false;
  isPlatformAdmin = false;
  userId = '';
  serviceManagers$!: Observable<ServiceManager[]>;

  constructor(
    private readonly router: Router,
    private readonly bookingService: BookingService,
    private readonly snackbarService: SnackbarService,
    private readonly userService: UserService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly cartridgeFacade: CartridgesFacade,
    private readonly destroyRef: DestroyRef
  ) {}

  /**
   * Action when click on the change button to open the dialog.
   */
  openDialog(): void {
    this.isLoading = true;
    this.showDialog = true;

    this.authenticatedUserService
      .getUserRoles()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(roles => roles.some(({ name }) => name === StaticRole.PLATFORM_ADMIN)),
        combineLatestWith(this.authenticatedUserService.getUser().pipe(map(({ id }) => id === this.serviceManagerId))),
        tap(([isPlatformAdmin, sameUserId]) => {
          this.isPlatformAdmin = isPlatformAdmin;
          this.isAuthorized = isPlatformAdmin || sameUserId;
        })
      )
      .subscribe({
        next: () => {
          if (this.isAuthorized) {
            this.serviceManagers$ = this.getServiceManagers(this.organizationId);
          }

          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  /**
   * Action when dialog save button is clicked.
   * @param serviceManager The new service manager data.
   */
  onSave(serviceManager: ServiceManager): void {
    this.isLoading = true;
    this.bookingService
      .editServiceManager(this.bookingId, serviceManager.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.isLoading = false;
          this.showDialog = false;
          this.serviceManager = serviceManager.name;
          this.snackbarService.showSuccessSnackbarWithTransloco(
            'cartridge.management.serviceResponsibility.toastSuccess'
          );
          this.serviceManagerUpdated.emit();
          this.cartridgeFacade.updateServiceManager(response.responsibleUserId as string, response.cartridgeId);
        },
        error: (error: HttpErrorResponse) => {
          const traceId = error.error?.traceId ?? '-';

          this.isLoading = false;
          this.showDialog = false;
          this.snackbarService.showErrorSnackbarWithTransloco('cartridge.management.serviceResponsibility.toastError', {
            traceId,
          });
        },
      });
  }

  /**
   * Close dialog action.
   */
  onCloseDialog(): void {
    this.showDialog = false;
  }

  /**
   * Jump to members view.
   */
  goToMembersView(): void {
    if (this.isPlatformAdmin) {
      this.router.navigate(['backoffice', 'organizations', this.organizationId, 'members']);
      return;
    }

    this.router.navigate(['organization', 'members']);
  }

  /**
   * Get all service managers, only with the role OA and active status.
   * @param organizationId The organization id.
   * @private
   */
  private getServiceManagers(organizationId: string): Observable<ServiceManager[]> {
    const filters = new URLSearchParams({
      role: StaticRole.ORGANIZATION_ADMIN,
      state: UserOrganizationStatus.ACTIVE,
    }).toString();

    return this.userService.getUsers(organizationId, undefined, undefined, undefined, filters).pipe(
      map(({ result }) => result.filter(user => user.id !== this.serviceManagerId)),
      map(users => users.map(user => ({ id: user.id, name: `${user.firstname} ${user.lastname}` }))),
      tap(() => (this.isLoading = false)),
      catchError(_ => {
        this.isLoading = false;
        return of([]);
      })
    );
  }

  private getEditButtonVisibility(bookingState: BookingState, usageRightSuspended: boolean): boolean {
    return (
      ![BookingState.DECLINED, BookingState.CANCELLED, BookingState.UPGRADED].includes(bookingState) &&
      !usageRightSuspended
    );
  }
}
